import React from 'react'

import { Formik } from 'formik'

import { ZariContext } from '_zari/context'

import { Form, Col, Row, Button, Divider, Alert } from '_zari/core'

import { OrigenesContabilidadConstant } from 'constants/index'

import { IdGen, ZrSwal, DownloadAxiosFile, RounderUtil } from 'utils'

import * as AsientosContablesService from 'services/asientosContables.service'

import * as FormGroups from './formGroups'
import * as Sections from './sections'

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'actualizar':
      return state.map(val => {
        if (val._idFila === action.payload._idFila) {
          val = {
            ...val,
            ...action.payload
          }
        }
        return val
      })
    case 'fill':
      return action.payload.map(x => ({
        ...x,
        _idFila: IdGen.newId()
      }))
    case 'limpiar':
      return []
    default:
      return state
  }
}

const MainForm = ({ resetMainForm }) => {
  const __zariContext = React.useContext(ZariContext)

  const [variasMonedas, setVariasMonedas] = React.useState(false)

  const [detalles, detallesDispatcher] = React.useReducer(commonReducer, [])

  const mainFormikRef = React.useRef(null)
  const formikPagoRef = React.useRef(null)

  React.useEffect(() => {
    if (formikPagoRef.current) {
      if (detalles.length > 0) {
        const monedas = [...new Set(detalles.filter(x => x.montoPago).map(x => x.moneda))]

        if (monedas.length === 0) {
          setVariasMonedas(false)

          formikPagoRef.current.setFieldValue('importeBase', '')
          formikPagoRef.current.setFieldValue('monedaBase', 'SOLES')
          formikPagoRef.current.setFieldValue('monedaPago', 'SOLES')
        } else if (monedas.length > 1) {
          setVariasMonedas(true)

          formikPagoRef.current.setFieldValue('importeBase', '')
          formikPagoRef.current.setFieldValue('monedaBase', 'SOLES')
          formikPagoRef.current.setFieldValue('monedaPago', 'SOLES')
        } else {
          setVariasMonedas(false)

          const suma = detalles.reduce(
            (accum, current) => RounderUtil.round(accum + parseFloat(current.montoPago || '0')),
            0
          )

          if (suma > 0) {
            formikPagoRef.current.setFieldValue('importeBase', suma)
            formikPagoRef.current.setFieldValue('monedaBase', monedas[0])
            formikPagoRef.current.setFieldValue('monedaPago', monedas[0])
          } else {
            formikPagoRef.current.setFieldValue('importeBase', '')
            formikPagoRef.current.setFieldValue('monedaBase', monedas[0])
            formikPagoRef.current.setFieldValue('monedaPago', monedas[0])
          }
        }
      } else {
        formikPagoRef.current.setFieldValue('importeBase', '')
        formikPagoRef.current.setFieldValue('monedaBase', 'SOLES')
        formikPagoRef.current.setFieldValue('monedaPago', 'SOLES')
      }
    }
  }, [detalles])

  const formInitialValues = React.useMemo(() => {
    return {
      _idSocio: '',
      codigoSocio: '',
      documentoSocio: '',
      nombreSocio: '',
      tipoDocumentoSocio: '',
      origen: OrigenesContabilidadConstant.COBRANZAS
    }
  }, [])

  const clickBuscarArchivos = React.useCallback(
    ({ _idSocio, origen }) => {
      __zariContext.loader.open('Buscando documentos pendientes')
      AsientosContablesService.getPendientesxSocioxOrigen(
        _idSocio,
        origen === OrigenesContabilidadConstant.COBRANZAS
          ? OrigenesContabilidadConstant.VENTAS
          : OrigenesContabilidadConstant.COMPRAS
      )
        .then(({ data }) => {
          __zariContext.loader.close()

          if (data && data.length > 0) {
            detallesDispatcher({ type: 'fill', payload: data })
          } else {
            __zariContext.snack('warning', 'El cliente indicado no cuenta con documentos pendientes.')
          }
        })
        .catch(() => {
          __zariContext.snack('error', 'No se pudo obtener los documentos pendientes del cliente.')
          __zariContext.loader.close()
        })
    },
    [__zariContext]
  )

  const onSubmit = React.useCallback(
    (pagoData, { setSubmitting }) => {
      const formData = {
        ...mainFormikRef.current.values,
        pago: pagoData,
        detalles: detalles
          .filter(x => x.montoPago)
          .map(x => ({
            _id: x._id,
            montoPago: parseFloat(x.montoPago)
          }))
      }

      AsientosContablesService.nuevoAsientoContableCobranza(formData)
        .then(({ data }) => {
          AsientosContablesService.descargarPdfTicketCobranzas(data.hash)
            .then(({ data: data2, headers }) => {
              DownloadAxiosFile.downloadAsBlob(data2, headers)
            })
            .catch(({ data: { error } }) => __zariContext.snack('error', error))

          ZrSwal.showAlert({
            tipo: 'success',
            mensaje: (
              <>
                <span>Operaciones Generadas :</span> <br />
                {data.operacionesGeneradas.map((x, k) => (
                  <React.Fragment key={k}>
                    <span>
                      {k + 1}. {x}
                    </span>{' '}
                    <br />
                  </React.Fragment>
                ))}
              </>
            ),
            onSuccess: () => {
              resetMainForm()
            }
          })
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
          setSubmitting(false)
        })
    },
    [__zariContext, detalles, resetMainForm]
  )

  return (
    <>
      <Row>
        <Col md={7}>
          <Formik
            innerRef={mainFormikRef}
            initialValues={formInitialValues}
            validateOnBlur={false}
            validateOnChange={false}
            validateOnMount={false}
          >
            {({ errors, touched, isSubmitting, values, setValues, setFieldValue }) => (
              <>
                <Form>
                  <Row>
                    <Col md={8}>
                      <FormGroups.SocioFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors._idSocio}
                        iTouched
                        iValue={values.nombreSocio}
                        setValues={setValues}
                        values={values}
                        vOrigen={values.origen}
                        dispatch={detallesDispatcher}
                      />
                    </Col>
                    <Col md={4}>
                      <Button onClick={() => clickBuscarArchivos(values)} block>
                        Ver Documentos
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </>
            )}
          </Formik>
          {detalles.length > 0 && (
            <>
              <Divider />
              <Sections.DocumentosPendientesSection filas={detalles} dispatch={detallesDispatcher} />
              {variasMonedas && <Alert color='danger'>No se puede pagar documentos con distinta moneda.</Alert>}
            </>
          )}
        </Col>
        {detalles.length > 0 && (
          <Col md={5}>
            <Sections.FormularioPagosSection formikPagoRef={formikPagoRef} submitFunction={onSubmit} />
          </Col>
        )}
      </Row>
    </>
  )
}

export default React.memo(MainForm)
